import { useCallback, useEffect, useState } from "react";
import { Formik, Field, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import classNames from "classnames";
import { actions } from "../../Drawer/drawers.reducers";
import Drawer, { selectSelectedDrawer } from "../../Drawer";
import { BUTTON_TEXT, REGEX, EMPTY_STRING } from "../../../../constants";
import { OPERATIONS } from "../drawers.constants";

import Spinner, { SPINNER_NAMES } from "../../Spinner";
import {
  createIntegration,
  selectSelectedIntegration,
  updateIntegrationDetails,
  setSelectedIntegration,
} from "../../../../pages/Mulesoft";
import { VALIDATION_MESSAGES } from "../../../../constants";

const { S3_BUCKET_VALIDATION } = REGEX;
const { setSelectedDrawer } = actions;
const { SUBMIT } = BUTTON_TEXT;
const { CREATE_INTEGRATION } = SPINNER_NAMES;
const { UPDATE } = OPERATIONS;
const { MIN_CHAR_LENGTH, MAX_CHAR_LENGTH_FIFTY, NAME_SHOULD_CONTAIN, NAME_REQUIRED } = VALIDATION_MESSAGES

const CreateIntegrationDrawer = () => {
  const dispatch = useDispatch();
  const selectedDrawer = useSelector(selectSelectedDrawer);
  const selectedIntegration = useSelector(selectSelectedIntegration);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [initialValues, setInitialValues] = useState({
    name: EMPTY_STRING,
  });

  useEffect(() => {
    selectedIntegration && setInitialValues({ name: selectedIntegration.name });
  }, [selectedIntegration]);

  const handleClose = useCallback(
    (resetForm) => {
      dispatch(setSelectedDrawer(null));
      dispatch(setSelectedIntegration(null));
      resetForm();
    },
    [dispatch]
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, MIN_CHAR_LENGTH)
      .max(50, MAX_CHAR_LENGTH_FIFTY)
      .matches(
        S3_BUCKET_VALIDATION,
        NAME_SHOULD_CONTAIN
      )
      .required(NAME_REQUIRED),
  });
  const handleOnSubmit = useCallback(
    (values, { resetForm }) => {
      dispatch(
        selectedIntegration
          ? updateIntegrationDetails({
              id: selectedIntegration.id,
              integrationDetails: values,
            })
          : createIntegration(values)
      ).then((res) => {
        if (res.payload) {
          dispatch(setSelectedDrawer(null));
          resetForm();
        }
      });
    },
    [dispatch, selectedIntegration]
  );
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
      validateOnMount={true}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
        isValid,
      }) => {
        const footer = (
          <div className="p-6 border-t align-center border-gray-700 flex justify-end">
            <button
              type="button"
              className="mr-1 btn btn-primary-light "
              onClick={() => handleClose(resetForm)}
              disabled={isSpinnerActive}
            >
              Cancel
            </button>
            <button
              type={SUBMIT}
              className="btn btn-primary"
              disabled={isSpinnerActive || !isValid}
            >
              <Spinner
                name={CREATE_INTEGRATION}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {selectedIntegration ? UPDATE : SUBMIT}
              </Spinner>
            </button>
          </div>
        );

        return (
          <Form onSubmit={handleSubmit}>
            <Drawer
              position="right"
              heading={
                selectedIntegration
                  ? "Update Integration"
                  : "Create a new Integration"
              }
              subHeading={`${selectedIntegration ? "Update" : "Configure"}
              your Integration settings.`}
              handleClose={() => handleClose(resetForm)}
              selectedDrawer={selectedDrawer}
              footer={footer}
            >
              <div className="scrollbar flex-1 overflow-auto">
                <div className="space-y-4 p-6 border-b border-gray-700">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Integration Name
                    </label>
                    <Field
                      name="name"
                      type="text"
                      className={classNames("form-control border-2", {
                        "border-red-600": touched.name && errors.name,
                      })}
                      placeholder="Integration Name"
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    {touched.name && errors.name && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Drawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateIntegrationDrawer;
